import { Box, Typography, Button, Alert, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  Icon,
  Text,
  MobileInput,
  MobileSelect,
  MobileButton,
  Background,
} from '@/components/ui';
import { Layout, Header } from '@/components/layout';
import { logoutIcon } from '@/assets';
import { useVersionNotifier } from '@/hooks';

import { useSettings, useAppInfo } from '../hooks';
import { AppInfoComponent } from '../components';

export const SettingsPage = () => {
  const { t } = useTranslation();
  const {
    userSettings,
    availableLanguages,
    loading,
    error,
    changeLanguage,
    handleLogout,
  } = useSettings();
  const { appInfo } = useAppInfo();
  const {
    hasUpdate,
    isUpdating,
    canInstall,
    isInstalling,
    isInstalled,
    isReady,
    reloadForUpdate,
    installApp,
    debugInstallState,
  } = useVersionNotifier();

  const handleLanguageChange = (value: string) => {
    changeLanguage(value);
  };
  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          minHeight: ['100vh', '100dvh', '100svh'],
          pb: 8,
        }}
      >
        <Header title={t('title_settings_page')} hideBackButton={false} />

        <Box>
          {' '}
          {/* 24px */}
          {/* User Name Section */}
          <MobileInput
            label={t('user_name_settings_page')}
            name='user_name'
            error={false}
            helperText=''
            onChange={() => {}}
            value={userSettings?.userName || ''}
            disabled
            compact
            placeholder=''
          />
          {/* Language Selection Section */}
          <MobileSelect
            title={t('language_selection_settings_page')}
            items={availableLanguages}
            onChange={handleLanguageChange}
            value={userSettings?.language || 'en'}
            disabled={loading}
          />
          {/* Logout Button */}
          <MobileButton
            btnColor='#DC3545'
            onClick={handleLogout}
            disabled={loading}
            sx={{ mt: 2 }}
          >
            <Icon src={logoutIcon} sx={{ mx: '0.25rem' }} /> {/* 4px */}
            <Text sx={{ color: 'white' }}>{t('logout_settings_page')}</Text>
          </MobileButton>
          {/* Version Info Section */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant='h6' sx={{ mb: 2 }}>
              {t('version_notifier.app_version_pwa_status_title')}
            </Typography>

            {/* Version Display */}
            <Alert
              severity='info'
              color='success'
              sx={{
                mb: 2,
                bgcolor: 'white',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Typography variant='body2'>
                  {t('version_notifier.version_fe_label')}{' '}
                  {typeof __APP_VERSION__ !== 'undefined'
                    ? __APP_VERSION__
                    : 'Unknown'}
                </Typography>
                <Typography variant='body2'>
                  {t('version_notifier.build_label')}{' '}
                  {typeof __BUILD_TIME__ !== 'undefined'
                    ? new Date(__BUILD_TIME__).toLocaleDateString()
                    : 'Unknown'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Typography variant='body2'>
                  {t('version_notifier.version_be_label')}{' '}
                  {appInfo?.buildVersion}
                </Typography>
                <Typography variant='body2'>
                  {t('version_notifier.build_label')}{' '}
                  {typeof appInfo?.buildDateTime !== 'undefined'
                    ? new Date(appInfo?.buildDateTime).toLocaleDateString()
                    : 'Unknown'}
                </Typography>
              </Box>
            </Alert>

            {/* PWA Status */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Alert
                severity={isReady ? 'success' : 'warning'}
                sx={{
                  flex: 1,
                  minWidth: 120,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant='caption'>
                  {t('version_notifier.service_worker_label')}{' '}
                  {isReady
                    ? t('version_notifier.service_worker_ready')
                    : t('version_notifier.service_worker_loading')}
                </Typography>
              </Alert>

              <Alert
                severity={
                  isInstalled ? 'success' : canInstall ? 'info' : 'info'
                }
                color='success'
                sx={{
                  flex: 1,
                  minWidth: 120,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant='caption'>
                  {t('version_notifier.install_label')}{' '}
                  {isInstalled
                    ? t('version_notifier.install_installed')
                    : canInstall
                      ? t('version_notifier.install_available')
                      : t('version_notifier.install_not_supported')}
                </Typography>
              </Alert>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {hasUpdate && (
                <Button
                  variant='contained'
                  color='primary'
                  size='small'
                  onClick={reloadForUpdate}
                  disabled={isUpdating}
                >
                  {isUpdating
                    ? t('version_notifier.updating_button')
                    : t('version_notifier.update_available_button')}
                </Button>
              )}

              {canInstall && (
                <Button
                  variant='contained'
                  color='primary'
                  size='small'
                  onClick={installApp}
                  disabled={isInstalling}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '&:disabled': {
                      backgroundColor: 'action.disabledBackground',
                      color: 'action.disabled',
                    },
                  }}
                >
                  {isInstalling
                    ? t('version_notifier.installing')
                    : t('version_notifier.install_app')}
                </Button>
              )}
            </Box>

            {/* Debug Section (Development Only) */}
            {import.meta.env.DEV && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant='h6' sx={{ mb: 1, fontSize: '0.875rem' }}>
                  {' '}
                  {/* 14px */}
                  Debug PWA State
                </Typography>
                <Button
                  variant='text'
                  size='small'
                  onClick={debugInstallState}
                  sx={{ mr: 1 }}
                >
                  Log PWA State
                </Button>
                <Typography variant='caption' sx={{ display: 'block', mt: 1 }}>
                  canInstall: {canInstall ? 'true' : 'false'} | isInstalled:{' '}
                  {isInstalled ? 'true' : 'false'} | isReady:{' '}
                  {isReady ? 'true' : 'false'}
                </Typography>
              </Box>
            )}
          </Box>
          {/* App Info Section */}
          <AppInfoComponent />
          {/* Error Display */}
          {error && (
            <Box
              sx={{ mt: 2, p: 2, backgroundColor: '#ffebee', borderRadius: 1 }}
            >
              <Text sx={{ color: '#d32f2f' }}>{error}</Text>
            </Box>
          )}
        </Box>

        <Background />
      </Box>
    </Layout>
  );
};
